import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertCircle, Clock, CheckCircle, XCircle, AlertTriangle, Wallet, ChevronRight, RefreshCw, DollarSign } from 'lucide-react';
import axios from 'axios';

const CashoutView = ({ setCurrentView, userDetails, token, calculateTotalBalance, setToastNotification }) => {
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasInvestments, setHasInvestments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresProof, setRequiresProof] = useState(false);
  const [activeTab, setActiveTab] = useState('withdraw');
  const [animateBalance, setAnimateBalance] = useState(false);
  
  // Modern Styles
  const styles = {
    container: {
      backgroundColor: '#111827',
      color: '#F9FAFB',
      padding: '24px',
      fontFamily: 'Inter, system-ui, sans-serif',
      borderRadius: '20px',
      maxWidth: '800px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '28px',
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      color: '#F9FAFB',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    subtitle: {
      fontSize: '15px',
      color: '#9CA3AF',
      fontWeight: '400',
      letterSpacing: '0.01em',
    },
    tabs: {
      display: 'flex',
      backgroundColor: 'rgba(31, 41, 55, 0.6)',
      borderRadius: '16px',
      padding: '6px',
      marginBottom: '28px',
      backdropFilter: 'blur(8px)',
    },
    tab: {
      flex: 1,
      padding: '14px',
      textAlign: 'center',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.25s ease',
    },
    activeTab: {
      backgroundColor: '#4F46E5',
      color: 'white',
      boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.25)',
    },
    inactiveTab: {
      color: '#9CA3AF',
    },
    balanceCard: {
      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
      borderRadius: '24px',
      padding: '32px',
      marginBottom: '32px',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 20px 25px -5px rgba(79, 70, 229, 0.15), 0 10px 10px -5px rgba(79, 70, 229, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
    },
    balanceLabel: {
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.8)',
      marginBottom: '10px',
      fontWeight: '500',
    },
    balanceAmount: {
      fontSize: '42px',
      fontWeight: '800',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    balanceAnimation: {
      animation: 'pulse 1.5s ease',
    },
    formContainer: {
      backgroundColor: 'rgba(31, 41, 55, 0.6)',
      borderRadius: '20px',
      padding: '28px',
      marginBottom: '28px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    formTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#F9FAFB',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    input: {
      width: '100%',
      backgroundColor: 'rgba(17, 24, 39, 0.8)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '16px',
      color: '#F9FAFB',
      marginBottom: '20px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      outline: 'none',
    },
    button: {
      width: '100%',
      backgroundColor: '#4F46E5',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      padding: '16px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.2s ease',
      boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.2)',
    },
    disabledButton: {
      backgroundColor: 'rgba(75, 85, 99, 0.5)',
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
    notice: {
      backgroundColor: 'rgba(251, 191, 36, 0.1)',
      borderLeft: '4px solid #F59E0B',
      borderRadius: '12px',
      padding: '18px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      backdropFilter: 'blur(4px)',
    },
    noticeIcon: {
      color: '#F59E0B',
      flexShrink: 0,
    },
    noticeText: {
      fontSize: '15px',
      color: '#F9FAFB',
      lineHeight: 1.6,
    },
    errorContainer: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      borderLeft: '4px solid #EF4444',
      borderRadius: '12px',
      padding: '18px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      backdropFilter: 'blur(4px)',
    },
    errorIcon: {
      color: '#EF4444',
      flexShrink: 0,
    },
    errorText: {
      fontSize: '15px',
      color: '#F9FAFB',
      lineHeight: 1.6,
    },
    linkButton: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#818CF8',
      cursor: 'pointer',
      padding: '0',
      fontSize: 'inherit',
      fontWeight: '600',
      textDecoration: 'underline',
      marginLeft: '4px',
      transition: 'color 0.2s ease',
    },
    historyContainer: {
      backgroundColor: 'rgba(31, 41, 55, 0.6)',
      borderRadius: '20px',
      overflow: 'hidden',
      marginBottom: '28px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    historyHeader: {
      padding: '20px 24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      fontSize: '20px',
      fontWeight: '600',
      color: '#F9FAFB',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    refreshButton: {
      backgroundColor: 'rgba(31, 41, 55, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      color: '#9CA3AF',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      gap: '6px',
      padding: '8px 12px',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
    },
    historyList: {
      padding: '0',
      maxHeight: '450px',
      overflowY: 'auto',
    },
    historyEmpty: {
      padding: '32px',
      textAlign: 'center',
      color: '#9CA3AF',
      fontSize: '16px',
    },
    historyItem: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      padding: '20px 24px',
      transition: 'background-color 0.2s ease',
    },
    historyItemHover: {
      backgroundColor: 'rgba(31, 41, 55, 0.5)',
    },
    historyItemHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '14px',
    },
    statusPill: {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    pendingStatus: {
      backgroundColor: 'rgba(251, 191, 36, 0.15)',
      color: '#F59E0B',
    },
    approvedStatus: {
      backgroundColor: 'rgba(16, 185, 129, 0.15)',
      color: '#10B981',
    },
    rejectedStatus: {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      color: '#EF4444',
    },
    historyDate: {
      fontSize: '13px',
      color: '#9CA3AF',
    },
    historyBody: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '14px',
    },
    amountColumn: {
      display: 'flex',
      flexDirection: 'column',
    },
    amountLabel: {
      fontSize: '13px',
      color: '#9CA3AF',
      marginBottom: '6px',
    },
    amountValue: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#F9FAFB',
    },
    historyFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '13px',
      color: '#9CA3AF',
    },
    orderNumber: {
      fontFamily: 'monospace',
      backgroundColor: 'rgba(31, 41, 55, 0.5)',
      padding: '4px 8px',
      borderRadius: '6px',
    },
    balanceCardDecoration: {
      position: 'absolute',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
    },
    circle1: {
      width: '250px',
      height: '250px',
      top: '-100px',
      right: '-50px',
    },
    circle2: {
      width: '180px',
      height: '180px',
      bottom: '-60px',
      right: '40px',
    },
    circle3: {
      width: '120px',
      height: '120px',
      bottom: '20px',
      left: '30px',
      opacity: '0.2',
    },
    processingSpinner: {
      animation: 'spin 1.2s linear infinite',
      marginRight: '8px',
    },
    shimmerContainer: {
      height: '180px',
      background: 'rgba(31, 41, 55, 0.4)',
      borderRadius: '20px',
      overflow: 'hidden',
      position: 'relative',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    shimmer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      animation: 'shimmer 2s infinite',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)',
    },
    walletIcon: {
      marginRight: '8px',
      color: 'rgba(255, 255, 255, 0.9)',
    },
    infoSection: {
      backgroundColor: 'rgba(31, 41, 55, 0.6)',
      borderRadius: '20px',
      padding: '28px',
      marginBottom: '28px',
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.05)',
    },
    infoTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#F9FAFB',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    feeInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '14px 0',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
    feeLabel: {
      color: '#9CA3AF',
      fontSize: '15px',
    },
    feeValue: {
      color: '#F9FAFB',
      fontWeight: '500',
      fontSize: '15px',
    },
    timeInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '14px 0',
    },
    balanceSymbol: {
      fontSize: '24px',
      marginRight: '4px',
      opacity: '0.9',
    },
    balanceCardTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px',
    },
    balanceCardIcon: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '12px',
      padding: '10px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    balanceBottomRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      marginTop: '12px',
    },
    balanceUpdatedText: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.6)',
    }
  };

  useEffect(() => {
    Promise.all([
      fetchWithdrawalHistory(),
      checkInvestments(),
      checkProofRequirement()
    ]).finally(() => setIsLoading(false));
  }, []);

  const checkProofRequirement = async () => {
    try {
      const response = await axios.get(
        `https://api.novasoftwaves.com/api/withdrawals/proof-requirement/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setRequiresProof(response.data.requiresProof);
    } catch (error) {
      console.error('Error checking proof requirement:', error);
      setToastNotification({ 
        message: 'Failed to check proof requirement', 
        type: 'error' 
      });
    }
  };

  const checkInvestments = async () => {
    try {
      const response = await axios.get(
        `https://api.novasoftwaves.com/api/investments/user/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setHasInvestments(response.data.length > 0);
    } catch (error) {
      console.error('Error checking investments:', error);
      setToastNotification({ 
        message: 'Failed to check investment status', 
        type: 'error' 
      });
    }
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const response = await axios.get(
        `https://api.novasoftwaves.com/api/withdrawals/history/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setWithdrawalHistory(response.data);
    } catch (error) {
      console.error('Error fetching withdrawal history:', error);
      setToastNotification({ 
        message: 'Failed to fetch withdrawal history', 
        type: 'error' 
      });
    }
  };

  const handleWithdrawal = async () => {
    if (requiresProof) {
      setErrorMessage('You must submit a proof for your previous withdrawal before making a new request.');
      return;
    }
    if (!hasInvestments) {
      setErrorMessage('You need to have at least one investment before making a withdrawal. Browse our investment products to get started!');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const response = await axios.post(
        'https://api.novasoftwaves.com/api/withdrawals/request',
        {
          email: userDetails.email,
          amount: withdrawAmount
        },
        {
          headers: { 'Authorization': token }
        }
      );

      setToastNotification({ 
        message: 'Withdrawal request submitted successfully', 
        type: 'success' 
      });
      setWithdrawAmount('');
      await fetchWithdrawalHistory();
      
      // Trigger balance animation
      setAnimateBalance(true);
      setTimeout(() => setAnimateBalance(false), 1500);
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to process withdrawal request';
      setErrorMessage(errorMessage);
      setToastNotification({ message: errorMessage, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const refreshHistory = async () => {
    setIsLoading(true);
    await fetchWithdrawalHistory();
    setIsLoading(false);
  };

  const getStatusIcon = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending') return <Clock size={16} />;
    if (statusLower === 'approved') return <CheckCircle size={16} />;
    if (statusLower === 'rejected') return <XCircle size={16} />;
    return <AlertCircle size={16} />;
  };

  const getStatusStyle = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending') return styles.pendingStatus;
    if (statusLower === 'approved') return styles.approvedStatus;
    if (statusLower === 'rejected') return styles.rejectedStatus;
    return {};
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            <Wallet /> Wallet
          </h1>
          <p style={styles.subtitle}>Manage your funds and withdrawal requests</p>
        </div>
        
        <div style={styles.shimmerContainer}>
          <div style={styles.shimmer}></div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Wallet /> Wallet
        </h1>
        <p style={styles.subtitle}>Manage your funds and withdrawal requests</p>
      </div>
      
      <div style={styles.tabs}>
        <div 
          style={{
            ...styles.tab,
            ...(activeTab === 'withdraw' ? styles.activeTab : styles.inactiveTab)
          }}
          onClick={() => setActiveTab('withdraw')}
        >
          Withdraw
        </div>
        <div 
          style={{
            ...styles.tab,
            ...(activeTab === 'history' ? styles.activeTab : styles.inactiveTab)
          }}
          onClick={() => setActiveTab('history')}
        >
          History
        </div>
      </div>
      
      <div style={{
        ...styles.balanceCard,
        ...(animateBalance ? styles.balanceAnimation : {})
      }}>
        <div style={{...styles.balanceCardDecoration, ...styles.circle1}}></div>
        <div style={{...styles.balanceCardDecoration, ...styles.circle2}}></div>
        <div style={{...styles.balanceCardDecoration, ...styles.circle3}}></div>
        
        <div style={styles.balanceCardTop}>
          <div>
            <p style={styles.balanceLabel}>Available Balance</p>
          </div>
          <div style={styles.balanceCardIcon}>
            <Wallet size={24} color="white" />
          </div>
        </div>
        
        <h1 style={styles.balanceAmount}>
          <span style={styles.balanceSymbol}>KES</span> {calculateTotalBalance().toLocaleString()}
        </h1>
        
        <div style={styles.balanceBottomRow}>
          <div style={styles.balanceUpdatedText}>
            Last updated: {formatDate(new Date())}
          </div>
        </div>
      </div>
      
      {activeTab === 'withdraw' && (
        <>
          {!hasInvestments && (
            <div style={styles.notice}>
              <AlertCircle style={styles.noticeIcon} size={20} />
              <div>
                <p style={styles.noticeText}>
                  You need to have at least one active trade to make withdrawals.
                  <button 
                    style={styles.linkButton}
                    onClick={() => setCurrentView('investments')}
                  >
                    Browse trade opportunities
                  </button>
                </p>
              </div>
            </div>
          )}
          
          {requiresProof && (
            <div style={styles.notice}>
              <AlertTriangle style={styles.noticeIcon} size={20} />
              <div>
                <p style={styles.noticeText}>
                  You must submit a proof for your previous withdrawal.
                  <button 
                    style={styles.linkButton}
                    onClick={() => setCurrentView('proofs')}
                  >
                    Submit Proof
                  </button>
                </p>
              </div>
            </div>
          )}
          
          {errorMessage && (
            <div style={styles.errorContainer}>
              <XCircle style={styles.errorIcon} size={20} />
              <p style={styles.errorText}>{errorMessage}</p>
            </div>
          )}
          
          <div style={styles.formContainer}>
            <h2 style={styles.formTitle}>
              <DollarSign size={20} /> Withdraw Funds
            </h2>
            
            <input 
              type="number" 
              placeholder="Amount (Min: KES 100)" 
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              style={styles.input}
              disabled={!hasInvestments || requiresProof}
            />
            
            <button 
              style={{
                ...styles.button,
                ...(isProcessing || !withdrawAmount || Number(withdrawAmount) < 100 || !hasInvestments || requiresProof ? styles.disabledButton : {})
              }}
              onClick={handleWithdrawal}
              disabled={isProcessing || !withdrawAmount || Number(withdrawAmount) < 100 || !hasInvestments || requiresProof}
            >
              {isProcessing ? (
                <>
                  <RefreshCw size={20} style={styles.processingSpinner} />
                  Processing...
                </>
              ) : (
                'Withdraw Funds'
              )}
            </button>
          </div>
          
          <div style={styles.infoSection}>
            <h2 style={styles.infoTitle}>
              <AlertCircle size={20} /> Withdrawal Information
            </h2>
            
            <div style={styles.feeInfo}>
              <span style={styles.feeLabel}>Processing Fee</span>
              <span style={styles.feeValue}>5%</span>
            </div>
            
            <div style={styles.feeInfo}>
              <span style={styles.feeLabel}>Minimum Amount</span>
              <span style={styles.feeValue}>KES 100</span>
            </div>
            
            <div style={styles.timeInfo}>
              <span style={styles.feeLabel}>Processing Time</span>
              <span style={styles.feeValue}>1-3 Business Days</span>
            </div>
          </div>
        </>
      )}
      
      {activeTab === 'history' && (
        <div style={styles.historyContainer}>
          <div style={styles.historyHeader}>
            <h2 style={styles.formTitle}>Transaction History</h2>
            <button 
              style={styles.refreshButton}
              onClick={refreshHistory}
            >
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
          
          <div style={styles.historyList}>
            {withdrawalHistory.length === 0 ? (
              <div style={styles.historyEmpty}>
                No withdrawal history found
              </div>
            ) : (
              withdrawalHistory.map((request, index) => (
                <div key={index} style={styles.historyItem}>
                  <div style={styles.historyItemHeader}>
                    <div style={{
                      ...styles.statusPill,
                      ...getStatusStyle(request.withdrawalStatus)
                    }}>
                      {getStatusIcon(request.withdrawalStatus)}
                      {request.withdrawalStatus}
                    </div>
                    <span style={styles.historyDate}>
                      {formatDate(request.requestTime)}
                    </span>
                  </div>
                  
                  <div style={styles.historyBody}>
                    <div style={styles.amountColumn}>
                      <span style={styles.amountLabel}>Requested</span>
                      <span style={styles.amountValue}>
                        KES {request.requestedAmount.toLocaleString()}
                      </span>
                    </div>
                    
                    <div style={styles.amountColumn}>
                      <span style={styles.amountLabel}>To Receive</span>
                      <span style={styles.amountValue}>
                        KES {request.amountToReceive.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  <div style={styles.historyFooter}>
                    <span style={styles.orderNumber}>
                      #{request.orderNumber}
                    </span>
                    <span>
                      {new Date(request.requestTime).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.03); }
          100% { transform: scale(1); }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default CashoutView;